import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  Clock, 
  Calendar,
  ArrowRight,
  User,
  Tag,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { SEO } from '@/components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog/posts`);
        if (!response.ok) throw new Error('Failed to fetch posts');
        const data = await response.json();
        setPosts(data.posts || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO 
        title="Blog & Guides | GoCompareAI"
        description="Practical guides and insights on choosing and comparing AI platforms. No hype, no rankings - just honest, useful information."
        keywords="AI blog, AI guides, AI platform comparison, choose AI tool, AI for business"
      />
      <Header />
      
      <main className="flex-1 w-full">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-6">
              <BookOpen className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-foreground mb-4">
              Blog & Guides
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Practical guides and insights on choosing and comparing AI platforms. 
              No hype, no rankings — just honest, useful information.
            </p>
          </div>

          {/* Posts List */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <Card className="text-center py-12">
              <CardContent>
                <p className="text-muted-foreground">Unable to load posts. Please try again later.</p>
              </CardContent>
            </Card>
          ) : posts.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No blog posts available yet. Check back soon!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {posts.map((post, index) => (
                <Link 
                  key={post.id} 
                  to={`/blog/${post.slug}`}
                  className="block group"
                >
                  <Card className="transition-all duration-200 hover:shadow-md hover:border-primary/20">
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                        <div className="flex-1">
                          {/* Tags */}
                          {post.tags && post.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {post.tags.slice(0, 3).map(tag => (
                                <Badge key={tag} variant="secondary" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                          
                          {/* Title */}
                          <h2 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                            {post.title}
                          </h2>
                          
                          {/* Excerpt */}
                          <p className="text-muted-foreground mb-4 line-clamp-2">
                            {post.excerpt || post.meta_description}
                          </p>
                          
                          {/* Meta */}
                          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(post.published_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {post.reading_time} min read
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {post.author}
                            </span>
                          </div>
                        </div>
                        
                        <div className="sm:self-center">
                          <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-12 text-center">
            <Card className="bg-muted/50">
              <CardContent className="py-8">
                <h3 className="text-lg font-medium mb-2">Ready to find the right AI platform?</h3>
                <p className="text-muted-foreground mb-4">
                  Answer a few simple questions and get personalised recommendations.
                </p>
                <Button asChild>
                  <Link to="/guide">
                    Start the Guide
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
